import { Loader2 } from 'lucide-react'
import { format, formatDistanceToNow, parseISO } from 'date-fns'
import { pl } from 'date-fns/locale'

import { apiErrorMessage } from '@/lib/api'
import {
  type AdminAuditEntry,
  useSessionDetails,
} from './use-admin-session-mutations'

function fmtAbs(iso: string): string {
  return format(parseISO(iso), "d MMM yyyy 'o' HH:mm", { locale: pl })
}

function fmtRel(iso: string): string {
  return formatDistanceToNow(parseISO(iso), { addSuffix: true, locale: pl })
}

function fmtTime(iso: string | null | undefined): string {
  if (!iso) return '(otwarta)'
  try {
    return format(parseISO(iso), "d MMM 'o' HH:mm", { locale: pl })
  } catch {
    return iso
  }
}

type SessionSnapshot = {
  startedAt?: string
  endedAt?: string | null
  notes?: string | null
  remoteEndPromptAt?: string | null
  remoteEndResolvedAt?: string | null
}

function describeAction(entry: AdminAuditEntry): string {
  const before = entry.beforeJson as SessionSnapshot | null
  const after = entry.afterJson as SessionSnapshot | null

  if (entry.action === 'session.create') {
    if (after?.startedAt) {
      return `Sesja dodana ręcznie (${fmtTime(after.startedAt)} → ${fmtTime(after.endedAt)})`
    }
    return 'Sesja dodana ręcznie'
  }

  if (entry.action === 'session.delete') {
    if (before?.startedAt) {
      return `Sesja usunięta (${fmtTime(before.startedAt)} → ${fmtTime(before.endedAt)})`
    }
    return 'Sesja usunięta'
  }

  if (entry.action === 'session.correct') {
    const changes: string[] = []
    if (before && after) {
      if (before.startedAt !== after.startedAt) {
        changes.push(`Start: ${fmtTime(before.startedAt)} → ${fmtTime(after.startedAt)}`)
      }
      const beforeEnd = before.endedAt ?? null
      const afterEnd = after.endedAt ?? null
      if (beforeEnd !== afterEnd) {
        changes.push(`Koniec: ${fmtTime(beforeEnd)} → ${fmtTime(afterEnd)}`)
      }
      const beforeNotes = before.notes ?? null
      const afterNotes = after.notes ?? null
      if (beforeNotes !== afterNotes) {
        changes.push('Notatka zaktualizowana')
      }
      // Korekta admina rozbraja pytanie o godzinę wyjścia (albo je kasuje przy
      // ponownym otwarciu sesji) — bez tej linijki historia pokazywałaby samą
      // zmianę godziny i nie byłoby widać, że pracownik już o nic nie zostanie
      // zapytany.
      if (before.remoteEndPromptAt && !after.remoteEndPromptAt) {
        changes.push('Anulowano pytanie o godzinę wyjścia')
      } else if (
        before.remoteEndPromptAt &&
        !before.remoteEndResolvedAt &&
        after.remoteEndResolvedAt
      ) {
        changes.push('Zamknięto pytanie o godzinę wyjścia')
      }
    }
    return changes.length > 0 ? changes.join(' • ') : 'Drobna korekta'
  }

  if (entry.action === 'session.auto_close_resolve') {
    const beforeEnd = before?.endedAt ?? null
    const afterEnd = after?.endedAt ?? null
    if (beforeEnd !== afterEnd) {
      return `Pracownik skorygował koniec: ${fmtTime(beforeEnd)} → ${fmtTime(afterEnd)}`
    }
    return 'Pracownik potwierdził automatyczną godzinę zakończenia'
  }

  if (entry.action === 'session.remote_end_resolve') {
    const beforeEnd = before?.endedAt ?? null
    const afterEnd = after?.endedAt ?? null
    if (beforeEnd !== afterEnd) {
      return `Pracownik skorygował koniec (poza placówką): ${fmtTime(beforeEnd)} → ${fmtTime(afterEnd)}`
    }
    return 'Pracownik potwierdził godzinę kliknięcia poza placówką'
  }

  return entry.action
}

export default function AuditTrail({ sessionId }: { sessionId: string }) {
  const { data, isLoading, error } = useSessionDetails(sessionId)

  if (isLoading) {
    return (
      <p className="text-sm text-text-muted inline-flex items-center gap-2">
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
        Wczytywanie historii…
      </p>
    )
  }
  if (error) {
    return (
      <p className="text-sm text-danger">
        Nie udało się załadować historii: {apiErrorMessage(error)}
      </p>
    )
  }
  if (!data || data.auditTrail.length === 0) {
    return <p className="text-sm text-text-muted">Brak historii zmian.</p>
  }

  return (
    <div className="mt-2 space-y-2">
      {data.auditTrail.map((entry) => (
        <div
          key={entry.id}
          className="border-l-2 border-border-subtle pl-3 py-1 text-sm"
        >
          <div className="text-xs text-text-muted">
            {entry.actor?.name ?? 'Admin'}{' '}
            <span title={fmtAbs(entry.createdAt)}>• {fmtRel(entry.createdAt)}</span>
          </div>
          <div className="text-text-primary">{describeAction(entry)}</div>
        </div>
      ))}
    </div>
  )
}
